import { proxy } from 'valtio';

import { Deferrable } from '@canvas/deferrable';
import {
  type FrameContext,
  type FrameState,
  type FrameToken,
} from '@canvas/frame-api-types';
import { type EntitlementFeatureBody } from '@canvas/paywall';
import { tokenManager } from '@canvas/token-manager';

const emptyState: FrameState = {
  container: '',
  board: '',
  accessToken: '',
  mediaToken: '',
  mediaBaseUrl: '',
  mediaCollectionId: '',
};

export const currentHostFrameContext = proxy<FrameContext>({
  data: emptyState,
});

// frameContextReady resolves when updateFrameContext() is first called
// await frameContextReady.get() if there's a chance that currentHostFrameContext may not be set yet
export const frameContextReady = new Deferrable<FrameContext>();

export const updateFrameContext = (token: FrameToken | undefined) => {
  currentHostFrameContext.data.container = token?.container ?? '';
  currentHostFrameContext.data.board = token?.board ?? '';
  currentHostFrameContext.data.accessToken = token?.accessToken ?? '';
  currentHostFrameContext.data.mediaToken = token?.mediaToken ?? '';
  currentHostFrameContext.data.mediaBaseUrl = token?.mediaBaseUrl ?? '';
  currentHostFrameContext.data.mediaCollectionId = token?.mediaCollectionId ?? '';
  currentHostFrameContext.data.editionOnlyUseForAnalytics =
    token?.editionOnlyUseForAnalytics;

  if (!isEmptyToken(token)) {
    tokenManager.updateToken(token);
  }

  frameContextReady.resolve(currentHostFrameContext);
};

export const updateFrameContainer = async (containerId: string) => {
  currentHostFrameContext.data.container = containerId;
  frameContextReady.resolve(currentHostFrameContext);
};

/**
 * If we have containerAri URL parameter, we initialize
 * the host frame context using them instead.
 */
export const updateFrameContextFromParams = () => {
  const href = globalThis.location?.href;
  const url = new URL(href);

  const containerAri = url.searchParams.get('containerAri');
  const boardAri = url.searchParams.get('boardAri');
  const embedded = url.searchParams.has('embedded');

  if (embedded) {
    currentHostFrameContext.data.container = containerAri ?? '';
    currentHostFrameContext.data.board = boardAri ?? '';
  }
};

export function resetFrameContext() {
  currentHostFrameContext.data = emptyState;
}

export const isEmptyToken = (token: FrameToken | undefined) => {
  return tokenManager.isEmpty(token);
};

export type AccessTokenBody = {
  token: string;
};

export type MediaTokenBody = {
  token: string;
  baseUrl: string;
  contentId: string;
};

export type RequestEmbeddedTokensResponse = {
  access: AccessTokenBody;
  media: MediaTokenBody;
  features?: EntitlementFeatureBody;
};
